/**
 * Sound (§6.5): "three samples: card slide, card place, turn chime. Off by
 * default."
 *
 * They are synthesised rather than loaded. Card sounds are broadband noise
 * shaped by an envelope and a filter, which is what a card actually is
 * acoustically, and synthesising them means there is no audio asset to fetch,
 * cache, mis-encode, or fail to load — the same reasoning as the inlined paper
 * grain. A fourth sound, the riffle shuffle at the deal, is added because the
 * deal is the one moment the table wants marking.
 *
 * Nothing plays until the setting is on *and* the player has interacted with
 * the page, because browsers will not start an AudioContext otherwise.
 */

export type Sound = 'shuffle' | 'slide' | 'place' | 'chime' | 'meld' | 'declare' | 'win';

/**
 * Two independent channels, because the sounds answer to two different
 * settings.
 *
 * `chime` is an *alert*: it tells you the turn is yours, and it is on by
 * default. Everything else is *atmosphere* — a card sliding, the riffle at the
 * deal — and stays opt-in. Gating them together is what made the one sound
 * that carries information inaudible on a table played with sound off, which is
 * every table by default.
 */
const CHANNEL: Record<Sound, 'alert' | 'effects'> = {
  chime: 'alert',
  shuffle: 'effects',
  slide: 'effects',
  place: 'effects',
  meld: 'effects',
  declare: 'effects',
  win: 'effects',
};

export class SoundBoard {
  private context: AudioContext | null = null;
  private noise: AudioBuffer | null = null;
  private master: GainNode | null = null;
  private effects = false;
  private alerts = true;
  /** Guards against a burst of events all firing the same sound at once. */
  private lastPlayedAt: Partial<Record<Sound, number>> = {};

  /** Card slide/place/shuffle and the declare and win stings. */
  setEnabled(enabled: boolean): void {
    this.effects = enabled;
    if (!enabled) return;
    // Safe to call repeatedly; resumes a context suspended by autoplay policy.
    void this.context?.resume();
  }

  /** The your-turn chime, independent of the above. */
  setAlertsEnabled(enabled: boolean): void {
    this.alerts = enabled;
    if (!enabled) return;
    void this.context?.resume();
  }

  private wants(sound: Sound): boolean {
    return CHANNEL[sound] === 'alert' ? this.alerts : this.effects;
  }

  /** Call from a real user gesture, or the context will never start. */
  unlock(): void {
    if (!this.effects && !this.alerts) return;
    this.ensure();
    void this.context?.resume();
  }

  private ensure(): AudioContext | null {
    if (this.context) return this.context;
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;

    const context = new Ctor();
    const master = context.createGain();
    master.gain.value = 0.32;
    master.connect(context.destination);

    // Two seconds of white noise, reused for every card sound.
    const buffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    this.context = context;
    this.master = master;
    this.noise = buffer;
    return context;
  }

  play(sound: Sound): void {
    if (!this.wants(sound)) return;
    const context = this.ensure();
    if (!context || context.state === 'suspended') return;

    const now = context.currentTime;
    const previous = this.lastPlayedAt[sound] ?? -1;
    if (now - previous < 0.06) return;
    this.lastPlayedAt[sound] = now;

    switch (sound) {
      case 'slide':
        // Card leaving the deck: a short, bright rasp.
        this.burst({ at: now, duration: 0.13, gain: 0.5, filter: 2600, q: 0.8, sweepTo: 1400 });
        return;
      case 'place':
        // Card meeting the table: rasp plus a soft body thump.
        this.burst({ at: now, duration: 0.07, gain: 0.6, filter: 1700, q: 1.1 });
        this.thump(now, 96, 0.11, 0.16);
        return;
      case 'shuffle':
        // A riffle is a run of small bursts, not one long hiss.
        for (let i = 0; i < 11; i++) {
          this.burst({
            at: now + i * 0.035 + Math.random() * 0.008,
            duration: 0.05,
            gain: 0.16 + Math.random() * 0.1,
            filter: 2200 + Math.random() * 1600,
            q: 1.4,
          });
        }
        this.thump(now + 0.42, 110, 0.12, 0.12);
        return;
      case 'chime': {
        // Your turn. Two tones, a fifth apart, soft attack.
        this.tone(now, 784, 0.28, 0.075);
        this.tone(now + 0.075, 1175, 0.34, 0.055);
        return;
      }
      case 'meld':
        // A meld closing. Deliberately not the turn chime: it is a confirmation
        // of something you just did, not a summons, and a table that says "your
        // turn" every time three cards line up has taught you to ignore it.
        // Quieter, higher, and a single tone rather than a rising pair.
        this.tone(now, 1568, 0.14, 0.05);
        return;
      case 'declare':
        // A clean upward signature when the table moves into Show.
        this.tone(now, 523.25, 0.24, 0.08);
        this.tone(now + 0.075, 659.25, 0.27, 0.1);
        this.tone(now + 0.15, 783.99, 0.31, 0.16);
        return;
      case 'win':
        // Kept bright and short: celebratory, never a casino fanfare.
        this.tone(now, 659.25, 0.24, 0.1);
        this.tone(now + 0.09, 783.99, 0.27, 0.12);
        this.tone(now + 0.19, 987.77, 0.32, 0.26);
        return;
    }
  }

  private burst(options: {
    at: number;
    duration: number;
    gain: number;
    filter: number;
    q: number;
    sweepTo?: number;
  }): void {
    const context = this.context;
    if (!context || !this.noise || !this.master) return;

    const source = context.createBufferSource();
    source.buffer = this.noise;
    source.playbackRate.value = 0.9 + Math.random() * 0.25;

    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(options.filter, options.at);
    if (options.sweepTo !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(
        options.sweepTo,
        options.at + options.duration,
      );
    }
    filter.Q.value = options.q;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, options.at);
    gain.gain.exponentialRampToValueAtTime(options.gain, options.at + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, options.at + options.duration);

    source.connect(filter).connect(gain).connect(this.master);
    source.start(options.at, Math.random() * 1.5, options.duration + 0.02);
    source.stop(options.at + options.duration + 0.03);
  }

  private thump(at: number, frequency: number, gain: number, duration: number): void {
    const context = this.context;
    if (!context || !this.master) return;
    const osc = context.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, at);
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.6, at + duration);

    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0.0001, at);
    envelope.gain.exponentialRampToValueAtTime(gain, at + 0.01);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    osc.connect(envelope).connect(this.master);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }

  private tone(at: number, frequency: number, gain: number, duration: number): void {
    const context = this.context;
    if (!context || !this.master) return;
    const osc = context.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = frequency;

    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0.0001, at);
    envelope.gain.exponentialRampToValueAtTime(gain, at + 0.02);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    osc.connect(envelope).connect(this.master);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }
}

export const sounds = new SoundBoard();
